import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../supabase';
import { listConversations, fetchProfilesByIds } from '../services/chat';

const MessageNotification = () => {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [newMessages, setNewMessages] = useState([]);
    const [showNotification, setShowNotification] = useState(false);
    const [conversations, setConversations] = useState([]);
    const [profiles, setProfiles] = useState({});
    const [activeConversationId, setActiveConversationId] = useState(null);
    const lastMessageIdsRef = useRef(new Set());
    const channelRef = useRef(null);

    const isOnMessagesPage = location.pathname.includes('/messages');

    useEffect(() => {
        if (isOnMessagesPage && location.state?.openConversationId) {
            setActiveConversationId(location.state.openConversationId);
        } else {
            setActiveConversationId(null);
        }
    }, [location, isOnMessagesPage]);

    useEffect(() => {
        if (!user?.id) return;

        const loadConversations = async () => {
            try {
                const convos = await listConversations(user.id);
                setConversations(convos);

                const participantIds = convos.flatMap((c) => [c.participant_a, c.participant_b]);
                const profs = await fetchProfilesByIds(participantIds);
                
                const profileMap = {};
                profs.forEach(p => {
                    profileMap[p.profileid] = p;
                });
                setProfiles(profileMap);
            } catch (error) {
                console.error('Error loading conversations:', error);
            }
        };

        loadConversations();
    }, [user?.id]);

    useEffect(() => {
        if (!user?.id || conversations.length === 0) return;

        if (channelRef.current) {
            supabase.removeChannel(channelRef.current);
        }

        const channel = supabase
            .channel(`user-messages:${user.id}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'messages',
                },
                async (payload) => {
                    const newMessage = payload.new;
                    
                    const conversation = conversations.find(
                        c => c.id === newMessage.conversation_id
                    );

                    if (!conversation) return;
                    if (newMessage.sender_id === user.id) return;
                    if (activeConversationId === newMessage.conversation_id && isOnMessagesPage) {
                        return;
                    }
                    if (lastMessageIdsRef.current.has(newMessage.id)) {
                        return;
                    }

                    lastMessageIdsRef.current.add(newMessage.id);

                    const senderProfile = profiles[newMessage.sender_id] || {};
                    const senderName = senderProfile.companyname || 
                                    `${senderProfile.firstname || ''} ${senderProfile.lastname || ''}`.trim() || 
                                    'Someone';

                    setNewMessages(prev => {
                        if (prev.some(m => m.id === newMessage.id)) {
                            return prev;
                        }
                        return [{
                            id: newMessage.id,
                            conversation_id: newMessage.conversation_id,
                            sender_id: newMessage.sender_id,
                            sender_name: senderName,
                            content: newMessage.content,
                            created_at: newMessage.created_at,
                        }, ...prev].slice(0, 5);
                    });

                    setShowNotification(true);

                    setTimeout(() => {
                        setShowNotification(false);
                    }, 5000);
                }
            )
            .subscribe();

        channelRef.current = channel;

        return () => {
            if (channelRef.current) {
                supabase.removeChannel(channelRef.current);
                channelRef.current = null;
            }
        };
    }, [user?.id, conversations, profiles, activeConversationId, isOnMessagesPage]);

    const handleNotificationClick = (conversationId) => {
        setShowNotification(false);
        setNewMessages([]);
        lastMessageIdsRef.current.clear();
        
        const path = location.pathname.startsWith('/employee') 
            ? '/employee/messages' 
            : '/employer/messages';
        
        navigate(path, { 
            state: { openConversationId: conversationId } 
        });
    };

    const formatTimeAgo = (timestamp) => {
        const now = new Date();
        const messageTime = new Date(timestamp);
        const diffInSeconds = Math.floor((now - messageTime) / 1000);

        if (diffInSeconds < 60) return 'just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        return `${Math.floor(diffInSeconds / 86400)}d ago`;
    };

    const truncateMessage = (content, maxLength = 50) => {
        if (content === null || content === undefined) return '';

        let baseText = '';
        if (typeof content === 'string') {
            baseText = content;
        } else if (typeof content === 'object') {
            try {
                baseText = JSON.stringify(content);
            } catch {
                baseText = String(content);
            }
        } else {
            baseText = String(content);
        }

        // Strip HTML tags for preview
        const textContent = baseText.replace(/<[^>]+>/g, '').trim();
        if (textContent.length <= maxLength) return textContent;
        return textContent.substring(0, maxLength) + '...';
    };

    if (!showNotification || newMessages.length === 0) {
        return null;
    }

    return (
        <div className="fixed top-4 right-4 z-50 max-w-sm">
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 animate-slide-in">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <div className="flex items-center mb-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></div>
                            <h3 className="text-sm font-semibold text-gray-900">New Message</h3>
                        </div>
                        
                        <div className="space-y-2">
                            {newMessages.slice(0, 3).map((message) => (
                                <div 
                                    key={message.id} 
                                    className="text-sm cursor-pointer hover:bg-gray-50 p-2 rounded -mx-2"
                                    onClick={() => handleNotificationClick(message.conversation_id)}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <span className="text-gray-900 font-medium">
                                                {message.sender_name}
                                            </span>
                                            <span className="text-gray-600 ml-1">
                                                sent a message
                                            </span>
                                        </div>
                                        <span className="text-gray-400 ml-2 text-xs">
                                            {formatTimeAgo(message.created_at)}
                                        </span>
                                    </div>
                                    <div className="text-gray-500 text-xs mt-1">
                                        {truncateMessage(message.content)}
                                    </div>
                                </div>
                            ))}
                            
                            {newMessages.length > 3 && (
                                <div className="text-xs text-gray-500">
                                    +{newMessages.length - 3} more messages
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <button
                        onClick={() => {
                            setShowNotification(false);
                            setNewMessages([]);
                            lastMessageIdsRef.current.clear();
                        }}
                        className="ml-2 text-gray-400 hover:text-gray-600"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MessageNotification;

